import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Play, Settings2, CheckSquare, Square, X, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import NormalizedGroupPanel from '@/components/data-integration/NormalizedGroupPanel';
import {
  getNormalizedTableList,
  getNormalizedGroups,
  updateNormalizedTableMeta,
  type NormalizedTableListItem,
  type NormalizedGroup,
} from '@/services/data-integration-api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// ─── 状态徽章 ────────────────────────────────────────────────────

function SyncStatusBadge({ status, time }: { status: string | null; time: number | null }) {
  if (!status) return <span className="text-muted-foreground text-xs">—</span>;
  const colorMap: Record<string, string> = {
    completed: 'bg-green-100 text-green-700',
    running: 'bg-blue-100 text-blue-700',
    failed: 'bg-red-100 text-red-700',
    partial: 'bg-orange-100 text-orange-700',
  };
  const labelMap: Record<string, string> = {
    completed: '已同步', running: '同步中', failed: '失败', partial: '部分成功',
  };
  const dateStr = time ? (() => {
    const d = new Date(time);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  })() : null;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium', colorMap[status] || 'bg-muted text-muted-foreground')}>
      {labelMap[status] || status}
      {dateStr && <span className="opacity-70">{dateStr}</span>}
    </span>
  );
}

// ─── 内联编辑单元格 ──────────────────────────────────────────────

function InlineEditCell({
  value,
  placeholder,
  onSave,
  multiline = false,
}: {
  value: string;
  placeholder: string;
  onSave: (v: string) => Promise<void>;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    if (multiline) {
      return (
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={e => { if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
          className="text-xs min-h-[60px] p-1 w-full border border-input rounded-md bg-background resize-none focus:outline-none focus:ring-1 focus:ring-ring"
          rows={3}
          disabled={saving}
          autoFocus
        />
      );
    }
    return (
      <Input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        className="h-7 text-xs px-1.5"
        disabled={saving}
        autoFocus
      />
    );
  }

  return (
    <button
      onClick={() => { setDraft(value); setEditing(true); }}
      className={cn(
        'text-left w-full truncate rounded px-1 py-0.5 hover:bg-accent transition-colors text-xs',
        value ? 'text-foreground' : 'text-muted-foreground italic'
      )}
      title={value || placeholder}
    >
      {value || placeholder}
    </button>
  );
}

// ─── 主组件 ──────────────────────────────────────────────────────

export default function NormalizedCatalog() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState<NormalizedGroup[]>([]);
  const [unclassifiedCount, setUnclassifiedCount] = useState(0);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null | undefined>(undefined); // undefined = 全部
  const [items, setItems] = useState<NormalizedTableListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // 多选批量操作
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchGroupId, setBatchGroupId] = useState<string>('');
  const [batchAssigning, setBatchAssigning] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const result = await getNormalizedGroups();
      setGroups(result.groups);
      setUnclassifiedCount(result.unclassified_count);
    } catch (e: any) {
      console.error('加载分组失败:', e.message);
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params: any = { pageSize: 100 };
      if (selectedGroupId !== undefined) params.groupId = selectedGroupId;
      const result = await getNormalizedTableList(params);
      setItems(result.items);
    } catch (e: any) {
      toast.error('加载失败: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // ─── 元数据更新 ──────────────────────────────────────────────

  const updateMeta = async (id: string, patch: Partial<Pick<NormalizedTableListItem, 'display_name' | 'description' | 'group_id' | 'sort_order'>>) => {
    await updateNormalizedTableMeta({ updates: [{ normalizedTableId: id, ...patch }] });
    setItems(prev => prev.map(t => t._id === id ? { ...t, ...patch } : t));
    loadGroups(); // 刷新计数
  };

  // ─── 选择操作 ────────────────────────────────────────────────

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(t => t._id)));
    }
  };

  const handleBatchAssign = async () => {
    if (selectedIds.size === 0) return;
    setBatchAssigning(true);
    try {
      const updates = [...selectedIds].map(id => ({
        normalizedTableId: id,
        group_id: batchGroupId === '__unclassified__' ? null : (batchGroupId || null),
      }));
      await updateNormalizedTableMeta({ updates });
      toast.success(`已将 ${selectedIds.size} 张表分配成功`);
      setSelectedIds(new Set());
      setBatchGroupId('');
      await Promise.all([loadItems(), loadGroups()]);
    } catch (e: any) {
      toast.error('批量分配失败: ' + e.message);
    } finally {
      setBatchAssigning(false);
    }
  };

  // ─── 分组折叠展示 ────────────────────────────────────────────

  const toggleCollapse = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // 将 items 按分组分区
  const groupedSections = (() => {
    if (selectedGroupId !== undefined) {
      // 过滤模式：只显示扁平列表
      return [{ key: '__filtered__', label: null, items }];
    }
    // 全部模式：按分组分区
    const grouped: Record<string, NormalizedTableListItem[]> = { __unclassified__: [] };
    groups.forEach(g => { grouped[g._id] = []; });
    items.forEach(t => {
      const gid = t.group_id || '__unclassified__';
      if (!grouped[gid]) grouped['__unclassified__'] = grouped['__unclassified__'] || [];
      (grouped[gid] || grouped['__unclassified__']).push(t);
    });

    const sections = groups.map(g => ({
      key: g._id,
      label: g.group_name,
      items: (grouped[g._id] || []).sort((a, b) => {
        if (a.sort_order != null && b.sort_order != null) return a.sort_order - b.sort_order;
        if (a.sort_order != null) return -1;
        if (b.sort_order != null) return 1;
        return (a.display_name || a.normalized_table_name).localeCompare(b.display_name || b.normalized_table_name);
      }),
    }));
    sections.push({ key: '__unclassified__', label: '未分类', items: grouped['__unclassified__'] || [] });
    return sections;
  })();

  // ─── 表格行 ──────────────────────────────────────────────────

  const renderRow = (t: NormalizedTableListItem) => (
    <TableRow key={t._id} className={cn('group/row', selectedIds.has(t._id) && 'bg-accent/30')}>
      <TableCell className="w-8 py-1.5">
        <Checkbox
          checked={selectedIds.has(t._id)}
          onCheckedChange={() => toggleSelect(t._id)}
          className="h-3.5 w-3.5"
        />
      </TableCell>
      <TableCell className="py-1.5 min-w-[140px]">
        <InlineEditCell
          value={t.display_name || ''}
          placeholder={t.normalized_table_name}
          onSave={async v => {
            await updateMeta(t._id, { display_name: v });
            toast.success('展示名称已保存');
          }}
        />
        <div className="text-[10px] text-muted-foreground font-mono pl-1">{t.normalized_table_name}</div>
      </TableCell>
      <TableCell className="py-1.5 min-w-[180px] max-w-[240px]">
        <InlineEditCell
          value={t.description || ''}
          placeholder="点击添加描述..."
          multiline
          onSave={async v => {
            await updateMeta(t._id, { description: v });
            toast.success('描述已保存');
          }}
        />
      </TableCell>
      <TableCell className="py-1.5 w-[130px]">
        <Select
          value={t.group_id || '__unclassified__'}
          onValueChange={async val => {
            const newGroupId = val === '__unclassified__' ? null : val;
            await updateMeta(t._id, { group_id: newGroupId });
            toast.success('分组已更新');
          }}
        >
          <SelectTrigger className="h-6 text-xs border-0 bg-transparent hover:bg-accent p-1 gap-1 focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__unclassified__" className="text-xs">未分类</SelectItem>
            {groups.map(g => (
              <SelectItem key={g._id} value={g._id} className="text-xs">{g.group_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="py-1.5 w-16 text-center">
        <SortOrderInput
          value={t.sort_order}
          onSave={async v => {
            await updateMeta(t._id, { sort_order: v });
          }}
        />
      </TableCell>
      <TableCell className="py-1.5 w-16 text-center">
        <span className="text-xs font-mono text-muted-foreground">v{t.schema_version}</span>
      </TableCell>
      <TableCell className="py-1.5 w-[110px]">
        <SyncStatusBadge status={t.last_sync_status} time={t.last_sync_time} />
      </TableCell>
      <TableCell className="py-1.5 w-[90px] text-center">
        {t.has_prod_mapping
          ? <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">已配置</Badge>
          : <span className="text-muted-foreground text-xs">—</span>}
      </TableCell>
      <TableCell className="py-1.5 w-20 text-right">
        {t.creation_status === 'created' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs opacity-0 group-hover/row:opacity-100"
            onClick={() => navigate(`/data-integration/sync/${t._id}`)}
          >
            <Play className="h-3 w-3 mr-1" />同步
          </Button>
        )}
      </TableCell>
    </TableRow>
  );

  // ─── 渲染 ────────────────────────────────────────────────────

  return (
    <div className="flex h-full overflow-hidden">
        {/* 左侧分组面板 */}
        <NormalizedGroupPanel
          groups={groups}
          unclassifiedCount={unclassifiedCount}
          selectedGroupId={selectedGroupId}
          onSelectGroup={id => setSelectedGroupId(id)}
          onGroupsChanged={() => { loadGroups(); loadItems(); }}
        />

        {/* 主内容区 */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* 顶部操作栏 */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
            <h2 className="text-base font-semibold">规范表型录</h2>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              共 {items.length} 张表
            </div>
          </div>

          {/* 批量操作栏 */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-2 bg-primary/5 border-b border-primary/20 text-sm">
              <CheckSquare className="h-4 w-4 text-primary" />
              <span className="font-medium text-primary">已选 {selectedIds.size} 张</span>
              <span className="text-muted-foreground">→ 分配到：</span>
              <Select value={batchGroupId} onValueChange={setBatchGroupId}>
                <SelectTrigger className="h-7 w-36 text-xs">
                  <SelectValue placeholder="选择分组..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__unclassified__" className="text-xs">移出分组（未分类）</SelectItem>
                  {groups.map(g => (
                    <SelectItem key={g._id} value={g._id} className="text-xs">{g.group_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="h-7 px-3 text-xs"
                disabled={!batchGroupId || batchAssigning}
                onClick={handleBatchAssign}
              >
                {batchAssigning ? '处理中...' : '确认分配'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setSelectedIds(new Set())}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}

          {/* 表格 */}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">加载中...</div>
            ) : items.length === 0 ? (
              <div className="flex items-center justify-center h-40 text-muted-foreground text-sm italic">暂无规范存取表</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-8 h-9 py-1.5">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size === items.length}
                        onCheckedChange={toggleSelectAll}
                        className="h-3.5 w-3.5"
                      />
                    </TableHead>
                    <TableHead className="h-9 py-1.5 text-xs">展示名称 / 物理表名</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs">描述摘要</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs">领域分组</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs text-center">排序</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs text-center">版本</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs">同步状态</TableHead>
                    <TableHead className="h-9 py-1.5 text-xs text-center">生产映射</TableHead>
                    <TableHead className="w-20 h-9 py-1.5" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedSections.map(section => (
                    <React.Fragment key={section.key}>
                      {/* 分组区标题行（全部视图下） */}
                      {section.label !== null && selectedGroupId === undefined && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={9} className="py-1 px-3">
                            <button
                              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                              onClick={() => toggleCollapse(section.key)}
                            >
                              {collapsedGroups.has(section.key)
                                ? <ChevronRight className="h-3.5 w-3.5" />
                                : <ChevronDown className="h-3.5 w-3.5" />}
                              {section.label}
                              <Badge variant="secondary" className="text-[10px] h-4 px-1">{section.items.length}</Badge>
                            </button>
                          </TableCell>
                        </TableRow>
                      )}
                      {!collapsedGroups.has(section.key) && section.items.map(renderRow)}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
    </div>
  );
}

// ─── 组内排序输入 ────────────────────────────────────────────────

function SortOrderInput({ value, onSave }: { value: number | null; onSave: (v: number | null) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value != null ? String(value) : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const num = draft === '' ? null : parseInt(draft, 10);
    if (isNaN(num as any) && draft !== '') { setEditing(false); return; }
    if (num === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(num);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <Input
        type="number"
        min={1}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={handleSave}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
        className="h-6 w-12 text-xs text-center p-1"
        disabled={saving}
        autoFocus
      />
    );
  }

  return (
    <button
      onClick={() => { setDraft(value != null ? String(value) : ''); setEditing(true); }}
      className="text-xs text-muted-foreground hover:text-foreground hover:bg-accent rounded px-1.5 py-0.5 min-w-[28px]"
    >
      {value != null ? value : '—'}
    </button>
  );
}
